/**
 * Una oferta como renglon de lista, para administrarla.
 *
 * POR QUE UN RENGLON Y NO LA TARJETA
 * ----------------------------------
 * Las ofertas se dibujaban como en la tienda: tarjetas de 248 px con los
 * controles abajo. Con diez —la mayoria plantillas apagadas— eran tres filas
 * de tarjetas grises para decir "esto no se ve". La tarjeta real queda arriba,
 * solo para las que se ven (`Asi se ven en la tienda`); aca cada oferta es un
 * renglon de 48 px con lo que hace falta para decidir: que tipo es, como se
 * llama, cuanto sale y si se ve.
 *
 * DOS CONTROLES, COMO TODA FILA DEL SISTEMA
 * -----------------------------------------
 * La casilla "Se ve" y el menu (Editar, Duplicar, Borrar). Borrar va en el
 * menu, nunca suelto (MASTER §6). Tocar el renglon tambien edita: es lo que se
 * quiere hacer con una oferta que no esta como se quiere.
 *
 * Las que se ven se ordenan arrastrando desde el asa (con el teclado: Espacio,
 * flechas, Espacio). Las apagadas no tienen orden que importe: no se ven.
 */
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { formatear } from "@tora/shared";
import { Copy, GripVertical, MoreHorizontal, Pencil, Trash2 } from "lucide-react";

import { etiquetaDeOferta } from "@/components/tienda/logica";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { IconButton } from "@/components/ui/icon-button";
import type { Oferta } from "@/lib/tipos";
import { cn } from "@/lib/utils";

/** Lo que la oferta promete, en corto: el precio, el descuento o nada. */
export function resumenDeOferta(o: Oferta): string {
  if (o.clase === "EFECTIVO" && o.porcentaje) return `${o.porcentaje} % off en efectivo`;
  if (o.precio !== null) return formatear(Number(o.precio) || 0, o.moneda);
  return o.clase === "CANJE" || o.clase === "ENVIO" ? "Se consulta" : "Sin precio";
}

export function FilaOferta({
  oferta: o,
  ordenable,
  ocupado,
  onEditar,
  onDuplicar,
  onBorrar,
  onPrender,
}: {
  oferta: Oferta;
  /** Solo las que se ven se arrastran: su orden es el del carrusel. */
  ordenable: boolean;
  ocupado: boolean;
  onEditar: () => void;
  onDuplicar: () => void;
  onBorrar: () => void;
  onPrender: (activa: boolean) => void;
}): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: o.id,
    disabled: !ordenable,
  });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "flex min-h-12 items-center gap-2 border-b border-border bg-card px-1 transition-colors duration-150 last:border-b-0",
        isDragging && "relative z-10 shadow-md",
      )}
    >
      {ordenable ? (
        <IconButton
          icono={GripVertical}
          tamano="sm"
          aria-label={`Mover la oferta ${o.titulo}`}
          className="shrink-0 cursor-grab touch-none text-muted-foreground active:cursor-grabbing"
          {...attributes}
          {...listeners}
        />
      ) : (
        // El mismo ancho que el asa, para que las columnas queden alineadas.
        <span className="size-8 shrink-0" aria-hidden="true" />
      )}

      {/* El renglon edita: titulo y precio son un solo objetivo grande. */}
      <div className="min-w-0 flex-1">
        <Button
          type="button"
          variant="ghost"
          onClick={onEditar}
          className="h-auto w-full flex-wrap justify-start gap-x-3 gap-y-1 px-2 py-2 text-left font-normal"
        >
          <Badge variant="outline" className="shrink-0">
            {etiquetaDeOferta(o.clase)}
          </Badge>
          {/* En el celular el titulo va en su propio renglon, entero, y el
              precio no entra: al lado del tipo y el precio quedaban tres
              letras. El precio se ve en la tarjeta de arriba y al editarla. */}
          <span
            className={cn(
              "order-last min-w-0 basis-full whitespace-normal text-sm font-medium sm:order-none sm:flex-1 sm:basis-0 sm:truncate",
              !o.activa && "text-muted-foreground",
            )}
          >
            {o.titulo}
          </span>
          <span className="hidden shrink-0 text-sm tabular-nums text-muted-foreground sm:inline">
            {resumenDeOferta(o)}
          </span>
        </Button>
      </div>

      <Checkbox
        label="Se ve"
        checked={o.activa}
        disabled={ocupado}
        onChange={(e) => onPrender(e.target.checked)}
        className="shrink-0 px-2"
      />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <IconButton
            icono={MoreHorizontal}
            tamano="sm"
            aria-label={`Más acciones de ${o.titulo}`}
            className="shrink-0"
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={onEditar}>
            <Pencil className="size-4" aria-hidden="true" />
            Editar
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={onDuplicar} disabled={ocupado}>
            <Copy className="size-4" aria-hidden="true" />
            Duplicar
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={onBorrar}>
            <Trash2 className="size-4" aria-hidden="true" />
            Borrar
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </li>
  );
}
